import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { tabbingOutsideOfElement } from '@chegg-ui/utils';
import { clickBaseEvent, viewBaseEvent } from '@chegg-me-components/utils';
import { rioClient } from './../../shared/rio';
import MegaMenu from './MegaMenu/MegaMenu';
import { OnboardingHeading } from './OnboardingHeading/OnboardingHeading';
import Slider from './Slider/Slider';
import StudyPackButton from './StudyPackButton/StudyPackButton';
import { useSetOnboardingComplete, useSetOnboardingFirstStepShown, useSetOnboardingIterationFrequency, useSetOnboardingMegaMenuNoAutoOpen, useSetOnboardingModalSeenTime } from './integrationHooks';
import { CSPOnboardingOverlay, CSPOnboardingWrapper, ModalWrapper } from './styles';
import { CSPOnboardingDataTestId, OnboardingType } from './types';
import { useCalculatedOffset } from './useCalculatedOffset';
import { useCurrentSlide } from './useCurrentSlide';
import { useDotIndicator } from './useDotIndicator';
import { useIsOpen } from './useIsOpen';
import { useMode } from './useMode';
export var CSPOnboardingHeadingDataTestId = 'csp-heading';
export var CSPOnboarding = function CSPOnboarding(props) {
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOnboardingDone = _useState2[0],
      setIsOnboardingDone = _useState2[1];

  var _useMode = useMode(_objectSpread(_objectSpread({}, props), {}, {
    isOnboardingDone: isOnboardingDone
  })),
      onboardingType = _useMode.onboardingType,
      autoOpen = _useMode.autoOpen,
      initialSlide = _useMode.initialSlide;

  var isCSPLiveOnboarding = onboardingType === OnboardingType.megaMenuLive || onboardingType === OnboardingType.sliderLive;
  var isMegaMenu = onboardingType === OnboardingType.megaMenuLive || onboardingType === OnboardingType.megaMenu;
  var isOnboarding = onboardingType === OnboardingType.sliderLive || onboardingType === OnboardingType.slider;

  var _useIsOpen = useIsOpen(autoOpen),
      _useIsOpen2 = _slicedToArray(_useIsOpen, 4),
      isOpen = _useIsOpen2[0],
      setIsOpen = _useIsOpen2[1],
      setDelayedIsOpen = _useIsOpen2[2],
      clearTimer = _useIsOpen2[3];

  var closeModal = useCallback(function () {
    setIsOpen(false);
  }, []);
  var debouncedOpenModal = useCallback(function () {
    setDelayedIsOpen(true);
  }, []);
  var debouncedCloseModal = useCallback(function () {
    setDelayedIsOpen(false);
  }, []);

  var _useCurrentSlide = useCurrentSlide(initialSlide),
      _useCurrentSlide2 = _slicedToArray(_useCurrentSlide, 2),
      currentSlide = _useCurrentSlide2[0],
      setCurrentSlide = _useCurrentSlide2[1];

  var setOnboardingComplete = useSetOnboardingComplete();
  var setOnboardingIterationFrequency = useSetOnboardingIterationFrequency();
  var setOnboardingModalSeenTime = useSetOnboardingModalSeenTime();
  useEffect(function () {
    if (isOpen && isOnboarding) {
      var lastSeenTime = Math.round(Date.now() / 1000);
      setOnboardingModalSeenTime(lastSeenTime);
      setOnboardingIterationFrequency();
    }
  }, [isOpen, isOnboarding]);
  var setOnboardingMegaMenuNoAutoOpen = useSetOnboardingMegaMenuNoAutoOpen();
  useEffect(function () {
    if (autoOpen && isMegaMenu) setOnboardingMegaMenuNoAutoOpen();
  }, [autoOpen, isMegaMenu]);
  var setOnboardingFirstStepShown = useSetOnboardingFirstStepShown();
  useEffect(function () {
    if (isOpen && isOnboarding && currentSlide === 0) setOnboardingFirstStepShown();
  }, [isOpen, currentSlide, isOnboarding]);

  var _useDotIndicator = useDotIndicator(),
      _useDotIndicator2 = _slicedToArray(_useDotIndicator, 2),
      isDotShown = _useDotIndicator2[0],
      removeDot = _useDotIndicator2[1];

  var onboardingRef = useRef(null);
  var buttonRef = useRef(null);

  var focusButton = function focusButton() {
    /* istanbul ignore else */
    if (buttonRef.current) buttonRef.current.focus();
  };

  var _useCalculatedOffset = useCalculatedOffset({
    ref: onboardingRef,
    isOpen: isOpen,
    onboardingType: onboardingType
  }),
      topOffset = _useCalculatedOffset.topOffset,
      modalOffset = _useCalculatedOffset.modalOffset,
      pointerOffset = _useCalculatedOffset.pointerOffset;

  var handleSliderDone = useCallback(function () {
    setIsOnboardingDone(true);
    setCurrentSlide(0);
    focusButton();
    closeModal();
    setOnboardingComplete();
    setOnboardingMegaMenuNoAutoOpen();
    debouncedOpenModal();
  }, []);

  var handleButtonClick = function handleButtonClick() {
    clearTimer();
    removeDot();
    /**
     * Rio logging for studypack button
     */

    rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
      data: {
        interaction: {
          type: 'expand',
          element: {
            name: 'nav study pack shortcut',
            type: 'drop down',
            region_name: "nav header"
          }
        }
      }
    }));
    /* istanbul ignore else */

    if (isMegaMenu || currentSlide === 0 || isOnboarding && !isOpen) setIsOpen(function (prevState) {
      return !prevState;
    });
  };

  var handleMouseOver = function handleMouseOver() {
    clearTimer();
    removeDot();
    if (!isOpen) setIsOpen(true);
  };

  var handleMouseLeave = function handleMouseLeave() {
    /**
     * Rio log for hover on
     * studypack button
     */
    rioClient.sendEvent(_objectSpread(_objectSpread({}, viewBaseEvent), {}, {
      data: {
        component_view: {
          component: {
            name: 'study pack',
            type: 'drop down'
          },
          state: 'expanded'
        }
      }
    }));
    /* istanbul ignore else */

    if (isOpen && isMegaMenu) debouncedCloseModal();
  };

  var handleKeyDown = function handleKeyDown(event) {
    /* istanbul ignore else */
    if (isMegaMenu) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.stopPropagation();
        closeModal();
        focusButton();
      }

      if (onboardingRef.current && tabbingOutsideOfElement(event, onboardingRef.current)) {
        closeModal();
      }
    }
    /* istanbul ignore else */


    if (isOnboarding) {
      if (onboardingRef.current && tabbingOutsideOfElement(event, onboardingRef.current)) {
        /* istanbul ignore else */
        if (currentSlide === 0) {
          closeModal();
          setCurrentSlide(1);
        } else if (isOpen) {
          event.stopPropagation();
          event.preventDefault();
        }
      }

      if (event.key === 'Escape' || event.key === 'Esc') {
        if (currentSlide === 0) {
          event.stopPropagation();
          closeModal();
          focusButton();
        }

        setCurrentSlide(function (prevVal) {
          return prevVal + 1;
        });
      }
    }
  };

  if (onboardingType === OnboardingType.none) {
    return null;
  }

  var dataTest = isMegaMenu ? CSPOnboardingDataTestId.megaMenu : CSPOnboardingDataTestId.slider;
  return /*#__PURE__*/React.createElement(CSPOnboardingWrapper, {
    onFocus: handleMouseOver,
    onMouseOver: handleMouseOver,
    onMouseLeave: handleMouseLeave,
    onKeyDown: handleKeyDown,
    ref: onboardingRef
  }, /*#__PURE__*/React.createElement(StudyPackButton, {
    ref: buttonRef,
    onClick: handleButtonClick,
    isCSPLiveOnboarding: isCSPLiveOnboarding,
    isDotShown: isDotShown,
    increaseZIndex: isOnboarding
  }), /*#__PURE__*/React.createElement(ModalWrapper, {
    isShown: isOpen,
    offset: modalOffset,
    topOffset: topOffset,
    "data-test": dataTest
  }, /*#__PURE__*/React.createElement(OnboardingHeading, {
    onboardingType: onboardingType,
    offset: pointerOffset,
    "data-test": CSPOnboardingHeadingDataTestId
  }), isOpen && isMegaMenu && /*#__PURE__*/React.createElement(MegaMenu, {
    isCSPLiveOnboarding: isCSPLiveOnboarding
  }), isOpen && isOnboarding && /*#__PURE__*/React.createElement(Slider, {
    isCSPLiveOnboarding: isCSPLiveOnboarding,
    currentSlide: currentSlide,
    onChangeSlide: setCurrentSlide,
    onClose: closeModal,
    onDone: handleSliderDone
  })), isOnboarding && isOpen && /*#__PURE__*/React.createElement(CSPOnboardingOverlay, null));
};
CSPOnboarding.displayName = 'CSPOnboarding';
export default CSPOnboarding;